/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 행 클래스를 정의한다.
 *
 * @file /src/models/table/Row.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 10.
 */
import { Table } from '@models/table/Table';
import { Record } from '@models/store/Record';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language } from '@utils/Language';
import './Row.css';

export interface Properties {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 자식행 인 경우 부모행
     */
    parent?: Row;

    /**
     * 데이터
     */
    record: Record;
}

export class Row {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 자식행 인 경우 부모행
     */
    parent: Row = null;

    /**
     * 데이터
     */
    record: Record;

    /**
     * 행 Dom
     */
    $row: Dom = null;

    /**
     * 컬럼 목록 Dom
     */
    $columns: Dom[] = [];

    /**
     * 레코드 Dom
     */
    $record: Dom = null;

    /**
     * 고정열 Dom
     */
    $freeze: Dom = null;

    /**
     * 트리 Dom
     */
    $branch: Dom = null;

    /**
     * 자식행
     */
    children: { [id: string]: Row } = {};

    /**
     * 레코드에 따라 ROW 에 추가된 클래스
     */
    rowClass: string[] = [];

    /**
     * 테이블 ROW 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        this.table = properties.table;
        this.record = properties.record;

        this.record.setObserver((record, fields, children) => {
            if (fields.length !== 0) {
                this.updateColumns(record, fields);
            }

            if (children == true) {
                this.renderBranch();
            }
        });
    }

    /**
     * 부모 ROW를 지정한다.
     *
     * @param parent - 부모가 존재할 경우 부모 ROW 객체
     */
    setParent(parent: Row = null): void {
        this.parent = parent;
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 레코드를 가져온다.
     *
     * @returns 레코드
     */
    getRecord(): Record {
        return this.record;
    }

    /**
     * 자식레코드로 자식행을 가져온다.
     *
     * @param child - 자식레코드
     * @returns 자식행
     */
    getChild(child: Record): Row {
        if (this.children[child.getId()] === undefined) {
            this.children[child.getId()] = this.getTable().getBody().getRow(child);
        }

        return this.children[child.getId()];
    }

    /**
     * 컬럼을 고정한다.
     */
    setFreeze(): void {
        this.$columns.some(($column, index) => {
            const column = this.getTable().getColumnAt(index);

            if (column.freeze == true) {
                this.$getFreeze().append($column);
            } else {
                this.$getRow().append($column);
            }
        });
    }

    /**
     * 행을 확장한다.
     *
     * @param expandedDepth - 추가적으로 확장할 트리뎁스 (true 인 경우 끝까지 확장하며, false 인 경우 현재행만 확장한다.)
     * @param is_remote_children - 자식 레코드 불러올 여부
     */
    async expand(expandedDepth: number | boolean = false, is_remote_children: boolean = true): Promise<void> {
        if (this.isExpanded() == true) {
            return;
        }

        const depth = this.record.getDepth();
        if (expandedDepth === false) {
            expandedDepth = this.record.getDepth() + 1;
        }

        if (typeof expandedDepth === 'number' && expandedDepth <= depth) {
            return;
        }

        if (this.getRecord().hasChildren(is_remote_children === false) == true) {
            const $button = Html.get(
                'div[data-model=table][data-role=column][data-index="0"] > i.toggle > button',
                this.$getRow()
            );
            $button.disable();

            const children = await this.record.getChildren();
            await this.renderBranch(children);

            this.$getTree().addClass('expand');
            await this.getTable().fireEvent('rowExpand', [this, this.getTable()]);

            for (const child of children) {
                const row = this.getChild(child);
                await row.expand(expandedDepth, is_remote_children);
            }

            $button.enable();
            await this.getTable().getSelection().notifyVisibilityChange();

            this.getTable().getBody().expanded.set(this.record.getId(), true);
        }
    }

    /**
     * 행을 축소한다.
     */
    async collapse(): Promise<void> {
        this.$getTree().removeClass('expand');
        await this.getTable().getSelection().notifyVisibilityChange();
        this.getTable().getBody().expanded.set(this.record.getId(), false);
    }

    /**
     * 행을 토글한다.
     */
    async toggle(): Promise<void> {
        if (this.isExpanded() == true) {
            await this.collapse();
        } else {
            await this.expand(false, true);
        }
    }

    /**
     * 행이 확장된 상태인지 확인한다.
     *
     * @returns 확장여부
     */
    isExpanded(): boolean {
        return this.getTable().isTree() == true && this.$getTree().hasClass('expand') == true;
    }

    /**
     * 컬럼 내용을 변경한다.
     *
     * @param record - 변경된 레코드
     * @param fields - 변경된 필드목록
     */
    updateColumns(record: Record, fields: string[] = []): void {
        this.record = record;

        this.getTable()
            .getColumns()
            .forEach((column, columnIndex) => {
                if (column.isUpdateNeeded(fields) == true) {
                    this.$columns[columnIndex] = column.update(this.record, columnIndex, this);
                }
            });

        this.updateRowClass();
    }

    /**
     * 레코드에 따라 ROW 클래스를 반영한다.
     */
    updateRowClass(): void {
        if (this.$record === null) return;

        if (this.rowClass.length > 0) {
            this.$record.removeClass(...this.rowClass);
        }

        const setRowClass = this.getTable().properties?.setRowClass ?? null;
        const rowClass = typeof setRowClass === 'function' ? setRowClass(this.record) : null;
        this.rowClass = (Array.isArray(rowClass) ? rowClass : typeof rowClass === 'string' ? rowClass.split(' ') : [])
            .map((className) => className.trim())
            .filter((className) => className !== '');

        if (this.rowClass.length > 0) {
            this.$record.addClass(...this.rowClass);
        }
    }

    /**
     * ROW Dom 을 가져온다. (트리 그리드 전용, 비트리 그리드는 null 반환)
     *
     * @returns ROW Dom
     */
    $getTree(): Dom | null {
        if (this.getTable().isTree() == false) {
            return null;
        }

        if (this.$row === null) {
            if (this.record.hasChildren() == true) {
                this.$row = Html.create('div', { 'data-model': 'table', 'data-role': 'tree' });
            } else {
                this.$row = Html.create('div', { 'data-model': 'table', 'data-role': 'list' });
            }

            this.$row.setAttr('data-id', this.record.getId());
        }

        return this.$row;
    }

    /**
     * 레코드 Dom 을 가져온다.
     *
     * @returns 레코드 Dom
     */
    $getRow(): Dom {
        if (this.$record === null) {
            this.$record = Html.create('div', { 'data-model': 'table', 'data-role': 'row' });
            this.$record.setAttr('data-record-id', this.record.getId());
            this.updateRowClass();

            this.$record.append(this.$getFreeze());

            if (this.getTable().getSelection().isMultiple() == true) {
                const $check = this.getTable().getSelection().$getBody(this);
                this.$getFreeze().append($check);
            }

            this.getTable()
                .getColumns()
                .forEach((column, columnIndex) => {
                    const $column = column.$getColumn(this.record, columnIndex, this);
                    this.$columns.push($column);

                    if (column.isHidden() == true) {
                        $column.addClass('hidden');
                    }

                    if (column.freeze == true) {
                        this.$getFreeze().append($column);
                    } else {
                        this.$record.append($column);
                    }
                });
        }

        return this.$record;
    }

    /**
     * 고정된 열이 들어가는 Dom 을 가져온다.
     *
     * @returns 고정된 열 Dom
     */
    $getFreeze(): Dom {
        if (this.$freeze === null) {
            this.$freeze = Html.create('div', { 'data-model': 'table', 'data-role': 'freeze' });
        }

        return this.$freeze;
    }

    /**
     * 가지 Dom 을 가져온다.
     *
     * @returns 트리 Dom
     */
    $getBranch(): Dom {
        if (this.$branch === null) {
            this.$branch = Html.create('div', { 'data-model': 'table', 'data-role': 'branch' });
        }

        return this.$branch;
    }

    /**
     * 자식을 렌더링한다.
     *
     * @param children - 자식레코드 (NULL 인 경우 자식을 불러온다.)
     */
    async renderBranch(children: Record[] = null): Promise<void> {
        if (this.isExpanded() == false && children === null) {
            return;
        }

        this.$getBranch().empty();

        children ??= await this.record.getChildren();

        const $branch = this.$getBranch();

        if (children.length > 0) {
            const selection = this.getTable().getSelection();

            for (const child of children) {
                const row = this.getChild(child);
                row.setParent(this);
                $branch.append(row.$getTree());
                row.connect();
                selection.syncSelectableRow(row);
            }

            $branch.removeAttr('data-empty-text');
            $branch.setStyle('padding-left', null);
        } else {
            if (this.getTable().getStore().getFilters() !== null) {
                if ($branch.hasClass('hasCheck') == false && this.getTable().getSelection().isMultiple() == true) {
                    $branch.addClass('hasCheck');
                }
                $branch.setAttr('data-empty-text', await Language.get('texts.empty_search_result').getText());
                $branch.setStyle('padding-left', `${this.record.getDepth() + 2}rem`);
            } else {
                $branch.removeAttr('data-empty-text');
                $branch.setStyle('padding-left', null);
            }
        }
    }

    /**
     * Row 연결
     */
    connect(): void {
        if (this.getTable().isTree() == true) {
            if (this.$getRow().isConnected() == false) {
                this.$getTree().setStyle('height', null);
                this.$getTree().append(this.$getRow());
                if (this.record.hasChildren() == true) {
                    this.$getTree().append(this.$getBranch());
                }
            }
        } else {
            this.$getRow();
        }
    }

    /**
     * Row 연결 해제
     */
    disconnect(): void {
        if (this.getTable().isTree() == true) {
            this.$getTree().setStyle('height', this.$getTree().getHeight());
            this.$getTree().empty();
        }
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.record.setObserver(null);
        this.table = null;
        this.record = null;
        this.$row = null;
        this.$columns = null;
        this.$record = null;
        this.$freeze = null;
        this.$branch = null;
        this.children = {};
        this.rowClass = [];
    }
}
